import { useMemo } from 'react';

import { type SxProps, type Theme, alpha, useTheme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

type StatusColor = 'success' | 'error' | 'pending';

export const useChipStatusColors = (): Record<StatusColor, string> => {
  const theme = useTheme();

  return useMemo(
    () => ({
      success: theme.palette.success.main,
      error: theme.palette.error.main,
      pending: theme.palette.warning.main,
    }),
    [theme],
  );
};

export const useChipStatusColor = ({ status }: { status: StatusColor }) => {
  const statusColorsMap = useChipStatusColors();

  return useMemo(() => statusColorsMap[status], [status, statusColorsMap]);
};

export const baseChipSx: SxProps<Theme> = {
  flexShrink: 0,
  cursor: 'default',
  px: 0.75,
  py: 0.25,
  borderRadius: '4px',
};

export const createChipStyle =
  (status: StatusColor) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    ...(baseChipSx as SystemStyleObject<Theme>),
    bgcolor: {
      success: alpha(theme.palette.success.main, 0.1),
      error: alpha(theme.palette.error.main, 0.1),
      pending: alpha(theme.palette.warning.light, 0.1),
    }[status],
  });

export const chipTypographyStyles = {
  fontWeight: 500,
} as const;
